import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { fetchEnvData } from "../services/envDataService";
import { CSVLink } from "react-csv";
 
export default function EnvDataDashboard() {
  const [envData, setEnvData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false); // Track if "select all" is checked
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState("");
  const [duration, setDuration] = useState("");
  const [industryType, setIndustryType] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [location, setLocation] = useState("");

 
  // industry_type group mappings
  const industryGroups = {
    Infrastructure: ["INFRA-1", "INFRA-2"],
    Industries: [
      "Industrial Projects - 1",
      "Industrial Projects - 2",
      "Industrial Projects - 3",
    ],
    Others: [
      "River Valley and Hydroelectric Project",
      "Thermal Projects",
      "Other",
    ],
  };
  const indianStates = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
    "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
    "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya",
    "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim",
    "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand",
    "West Bengal", "Delhi", "Puducherry", "Jammu and Kashmir", "Ladakh"
  ];

  // Fetch env data from API
  const loadEnvData = async () => {
    try {
      setLoading(true);
      const selectedIndustryValues = industryType
        ? industryGroups[industryType]
        : [];
 
      const filters = {
        project_name: searchQuery || "",
        created_at_from: startDate || "",
        created_at_to: endDate || "",
        duration_days:
          duration === "15 Days"
            ? 15
            : duration === "30 Days"
            ? 30
            : duration === "60 Days"
            ? 60
            : duration === "90 Days"
            ? 90
            : "",
        context: context || "",
        industry_type_list: selectedIndustryValues,
        location: location || "",
        page,
        limit: 10,
      };
 
      const res = await fetchEnvData(filters);
      if (res.success && res.data?.items) {
        setEnvData(res.data.items);
        setTotalRecords(res.data.total || 0);
        setTotalPages(Math.max(1, Math.ceil(res.data.total / 10)));
      } else {
        setEnvData([]);
        setTotalRecords(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setEnvData([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };
 
  useEffect(() => {
    loadEnvData();
  }, [page, context, duration, industryType,location]);
 
  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
    setSelectedRows([]);
    setSelectAll(false);
  }, [context, duration, industryType, searchQuery, startDate, endDate,location]);
 
  // Selection handlers
  const handleSelectAll = async (e) => {
    const isChecked = e.target.checked;
    setSelectAll(isChecked);
 
    if (isChecked) {
      // If checked, we need to fetch ALL data IDs from all pages
      setExportLoading(true);
      try {
        const selectedIndustryValues = industryType
          ? industryGroups[industryType]
          : [];
 
        const filters = {
          project_name: searchQuery || "",
          created_at_from: startDate || "",
          created_at_to: endDate || "",
          duration_days:
            duration === "15 Days"
              ? 15
              : duration === "30 Days"
              ? 30
              : duration === "60 Days"
              ? 60
              : duration === "90 Days"
              ? 90
              : "",
          context: context || "",
          industry_type_list: selectedIndustryValues,
          location: location || "",
          page: 1,
          limit: totalRecords, // Fetch all records
        };
 
        const res = await fetchEnvData(filters);
        if (res.success && res.data?.items) {
          const allIds = res.data.items.map((item) => item.id);
          setSelectedRows(allIds);
        }
      } catch (error) {
        console.error("Error fetching all data:", error);
      } finally {
        setExportLoading(false);
      }
    } else {
      setSelectedRows([]);
    }
  };
 
  const handleSelectRow = (id) => {
    setSelectedRows((prev) => {
      const newSelection = prev.includes(id)
        ? prev.filter((rowId) => rowId !== id)
        : [...prev, id];
     
      // Update selectAll checkbox state
      setSelectAll(false);
      return newSelection;
    });
  };
 
  // Fetch all data for export when needed
  const fetchAllDataForExport = async () => {
    setExportLoading(true);
    try {
      const selectedIndustryValues = industryType
        ? industryGroups[industryType]
        : [];
 
      const filters = {
        project_name: searchQuery || "",
        created_at_from: startDate || "",
        created_at_to: endDate || "",
        duration_days:
          duration === "15 Days"
            ? 15
            : duration === "30 Days"
            ? 30
            : duration === "60 Days"
            ? 60
            : duration === "90 Days"
            ? 90
            : "",
        context: context || "",
        industry_type_list: selectedIndustryValues,
        location: location || "",
        page: 1,
        limit: totalRecords, // Fetch all records
      };
 
      const res = await fetchEnvData(filters);
      if (res.success && res.data?.items) {
        return res.data.items.filter((item) =>
          selectedRows.includes(item.id)
        );
      }
      return [];
    } catch (error) {
      console.error("Error fetching export data:", error);
      return [];
    } finally {
      setExportLoading(false);
    }
  };
 
  // Export logic
  const [dataToExport, setDataToExport] = useState([]);
 
  const handleExportClick = async () => {
    if (selectedRows.length === 0) return;
   
    setExportLoading(true);
    const exportData = await fetchAllDataForExport();
    setDataToExport(exportData);
    setExportLoading(false);
  };
 
  const csvHeaders = [
    { label: "Project Name", key: "project_name" },
    { label: "Location", key: "location" },
    { label: "Proposal Number", key: "proposal_number" },
    { label: "Developer", key: "developer" },
    { label: "Consultant", key: "consultant" },
    { label: "Category", key: "category" },
    { label: "Application Type", key: "application_type" },
    { label: "Context", key: "context" },
    { label: "Context Size", key: "context_size" },
    { label: "Context Technology", key: "context_technology" },
    { label: "Industry Type", key: "industry_type" },
    { label: "Project date", key: "meeting_start_date" },

  ];
 
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
 
    // If only 1 page or no data, show just page 1
    if (totalPages === 1 || totalRecords === 0) {
      pages.push(1);
      return pages;
    }
 
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (page <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        pages.push(page - 1);
        pages.push(page);
        pages.push(page + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }
 
    return pages;
  };
 
  // Check if current page items are all selected
  const isCurrentPageAllSelected = envData.length > 0 && envData.every((item) => selectedRows.includes(item.id));
 
  return (
    <div className="p-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div>
          <h2 className="text-xl font-semibold select-none">
            Environmental Data
          </h2>
          {selectedRows.length > 0 && (
            <p className="text-sm text-teal-600 mt-1">
              {selectedRows.length} row{selectedRows.length !== 1 ? "s" : ""} selected
            </p>
          )}
        </div>
 
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          {/* Location Filter */}
          <select
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-teal-500"
          >
            <option value="">All States</option>
            {indianStates.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>

          {/* Context Filter */}
          <select
            value={context}
            onChange={(e) => setContext(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-teal-500"
          >
            <option value="">All Contexts</option>
            <option value="STP">STP</option>
            <option value="ETP">ETP</option>
            <option value="ZLD">ZLD</option>
          </select>
 
          {/* Duration Filter */}
          <select
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-teal-500"
          >
            <option value="">All Durations</option>
            <option value="15 Days">15 Days</option>
            <option value="30 Days">1 Month</option>
            <option value="60 Days">2 Months</option>
            <option value="90 Days">3 Months</option>
          </select>
 
          {/* Industry Type Filter */}
          <select
            value={industryType}
            onChange={(e) => setIndustryType(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-teal-500"
          >
            <option value="">All Industry Types</option>
            <option value="Infrastructure">Infrastructure</option>
            <option value="Industries">Industries</option>
            <option value="Others">Others</option>
          </select>
 
          {/* Export Button */}
          {dataToExport.length > 0 && selectedRows.length > 0 ? (
            <CSVLink
              data={dataToExport}
              headers={csvHeaders}
              filename={`env_data_export_${new Date().toISOString().split('T')[0]}.csv`}
              className="bg-teal-600 hover:bg-teal-700 text-white font-medium px-4 py-2 rounded-md flex items-center gap-2 justify-center"
              onClick={() => setDataToExport([])}
            >
              <Download size={18} /> Export CSV ({selectedRows.length})
            </CSVLink>
          ) : (
            <button
              onClick={handleExportClick}
              disabled={selectedRows.length === 0 || exportLoading}
              className={`font-medium px-4 py-2 rounded-md flex items-center gap-2 justify-center ${
                selectedRows.length === 0
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : exportLoading
                  ? "bg-teal-400 text-white cursor-wait"
                  : "bg-teal-600 hover:bg-teal-700 text-white"
              }`}
            >
              {exportLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Loading...
                </>
              ) : (
                <>
                  <Download size={18} /> Export CSV
                  {selectedRows.length > 0 && ` (${selectedRows.length})`}
                </>
              )}
            </button>
          )}
        </div>
      </div>
 
      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm table-fixed">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 w-14">
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={selectAll || isCurrentPageAllSelected}
                    ref={(input) => {
                      if (input) {
                        input.indeterminate = !selectAll && selectedRows.length > 0 && !isCurrentPageAllSelected;
                      }
                    }}
                    disabled={exportLoading}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-2 focus:ring-teal-500 cursor-pointer"
                    title={selectAll ? "Unselect all rows (all pages)" : "Select all rows (all pages)"}
                  />
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 w-56">
                  Project Name
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 w-48">
                  Location
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 w-44">
                  Proposal Number
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 w-40">
                  Developer
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 w-40">
                  Consultant
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 w-56">
                  Category
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 w-40">
                  Application Type
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 w-32">
                  Context
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 w-36">
                  Context Size
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 w-48">
                  Context Technology
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 w-48">
                  Industry Type
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 w-48">
                  Project date
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan="12" className="py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : envData.length > 0 ? (
                envData.map((item) => (
                  <tr
                    key={item.id}
                    className={`hover:bg-gray-50 transition-colors ${
                      selectedRows.includes(item.id) ? "bg-teal-50" : ""
                    }`}
                  >
                    <td className="px-4 py-3 text-center align-top">
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(item.id)}
                        onChange={() => handleSelectRow(item.id)}
                        className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-2 focus:ring-teal-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div
                        className="break-words line-clamp-3"
                        title={item.project_name || "-"}
                      >
                        {item.project_name || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div
                        className="break-words line-clamp-3"
                        title={item.location || "-"}
                      >
                        {item.location || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div
                        className="break-words line-clamp-2"
                        title={item.proposal_number || "-"}
                      >
                        {item.proposal_number || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div
                        className="break-words line-clamp-2"
                        title={item.developer || "-"}
                      >
                        {item.developer || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div
                        className="break-words line-clamp-2"
                        title={item.consultant || "-"}
                      >
                        {item.consultant || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div
                        className="break-words line-clamp-3"
                        title={item.category || "-"}
                      >
                        {item.category || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div
                        className="break-words line-clamp-2"
                        title={item.application_type || "-"}
                      >
                        {item.application_type || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div
                        className="break-words line-clamp-2"
                        title={item.context || "-"}
                      >
                        {item.context || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div
                        className="break-words line-clamp-2"
                        title={item.context_size || "-"}
                      >
                        {item.context_size || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div
                        className="break-words line-clamp-3"
                        title={item.context_technology || "-"}
                      >
                        {item.context_technology || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div
                        className="break-words line-clamp-2"
                        title={item.industry_type || "-"}
                      >
                        {item.industry_type || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div
                        className="break-words line-clamp-2"
                        title={item.meeting_start_date || "-"}
                      >
                        {item.meeting_start_date || "-"}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="12" className="text-center py-8 text-gray-500">
                    No data found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
 
      {/* Pagination */}
      {totalRecords > 0 && (
        <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-sm text-gray-600">
            Page {page} of {totalPages} ({totalRecords} total records)
          </div>
 
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="p-2 rounded-md border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
 
            <div className="flex items-center gap-1">
              {getPageNumbers().map((pageNum, index) =>
                pageNum === "..." ? (
                  <span key={`ellipsis-${index}`} className="px-2 text-gray-400">
                    ...
                  </span>
                ) : (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`min-w-[40px] h-10 px-3 rounded-md border transition-all ${
                      page === pageNum
                        ? "bg-teal-600 text-white border-teal-600 font-medium shadow-sm"
                        : "border-gray-300 hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              )}
            </div>
 
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              className="p-2 rounded-md border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              aria-label="Next page"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
 
          <div className="text-sm text-gray-600 hidden sm:block w-24">
            {/* Spacer for alignment */}
          </div>
        </div>
      )}
    </div>
  );
}